import axiosRestaurantInstance from './AxiosRestaurantInstance';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';

function authHeaders() {
    const token = localStorage.getItem('Token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function pickValue(obj, ...keys) {
    if (!obj || typeof obj !== 'object') return undefined;
    for (const key of keys) {
        const val = obj[key];
        if (val !== undefined && val !== null && val !== '') return val;
    }
    return undefined;
}

const VALID_QUOTE_STATUSES = new Set(['REQUESTED', 'UNDER_REVIEW', 'CONFIRMED', 'REJECTED']);
export const TERMINAL_PARTY_HALL_QUOTE_STATUSES = new Set(['CONFIRMED', 'REJECTED']);

export function normalizePartyHallQuoteStatusKey(quoteStatus) {
    return String(quoteStatus ?? '')
        .trim()
        .toUpperCase()
        .replace(/[\s_-]+/g, '');
}

export function isTerminalPartyHallQuoteStatus(quoteStatus) {
    return TERMINAL_PARTY_HALL_QUOTE_STATUSES.has(normalizePartyHallQuoteStatusKey(quoteStatus));
}

function resolveQuoteStatus(raw) {
    const picked = pickValue(raw, 'quoteStatus', 'Quote_Status');
    let status = String(picked ?? 'REQUESTED').trim().toUpperCase().replace(/\s+/g, '_');
    if (!status || !VALID_QUOTE_STATUSES.has(status)) {
        return 'REQUESTED';
    }
    return status;
}

export function normalizePartyHallQuote(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = pickValue(raw, 'partyHallQuoteId', 'Party_Hall_Quote_Id');
    if (id == null) return null;
    return {
        partyHallQuoteId: Number(id),
        customerId: pickValue(raw, 'customerId', 'Customer_Id'),
        customerName: pickValue(raw, 'customerName', 'Customer_Name') ?? '',
        customerPhone: pickValue(raw, 'customerPhone', 'Customer_Phone') ?? '',
        restaurantId: pickValue(raw, 'restaurantId', 'Restaurant_Id'),
        partyHallId: pickValue(raw, 'partyHallId', 'Party_Hall_Id'),
        hallName: pickValue(raw, 'hallName', 'Hall_Name') ?? '',
        hallType: pickValue(raw, 'hallType', 'Hall_Type') ?? '',
        eventDate: pickValue(raw, 'eventDate', 'Event_Date') ?? '',
        eventTime: pickValue(raw, 'eventTime', 'Event_Time') ?? '',
        eventType: pickValue(raw, 'eventType', 'Event_Type') ?? '',
        numberOfGuests: pickValue(raw, 'numberOfGuests', 'Number_Of_Guests'),
        amenityNotes: pickValue(raw, 'amenityNotes', 'Amenity_Notes') ?? '',
        specialRequests: pickValue(raw, 'specialRequests', 'Special_Requests') ?? '',
        estimatedTotal: pickValue(raw, 'estimatedTotal', 'Estimated_Total'),
        quotedTotal: pickValue(raw, 'quotedTotal', 'Quoted_Total'),
        quoteStatus: resolveQuoteStatus(raw),
        createdBy: pickValue(raw, 'createdBy', 'Created_By'),
        createdDate: pickValue(raw, 'createdDate', 'Created_Date') ?? '',
        updatedBy: pickValue(raw, 'updatedBy', 'Updated_By'),
        updatedDate: pickValue(raw, 'updatedDate', 'Updated_Date') ?? '',
    };
}

export const PARTY_HALL_QUOTES_LIST_PATH =
    '/home?tab=orders&orderType=partyHall&partyHallView=quotes';

export async function fetchPartyHallQuotesByRestaurant(restaurantId, { quoteStatus } = {}) {
    if (!restaurantId) return [];
    const params = {};
    if (quoteStatus) params.quoteStatus = quoteStatus;
    const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.partyHallQuotesByRestaurantId(restaurantId),
        { params, headers: authHeaders() }
    );
    const rows = res.data?.quotes ?? res.data?.Quotes ?? [];
    return Array.isArray(rows) ? rows.map(normalizePartyHallQuote).filter(Boolean) : [];
}

export async function fetchPartyHallQuoteById(quoteId) {
    const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.partyHallQuoteById(quoteId),
        { headers: authHeaders() }
    );
    return normalizePartyHallQuote(res.data?.quote ?? res.data);
}

export async function fetchPartyHallsByRestaurant(restaurantId) {
    if (!restaurantId) return [];
    const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.partyHallsByRestaurantId(restaurantId),
        { headers: authHeaders() }
    );
    const rows = res.data?.halls ?? [];
    return Array.isArray(rows) ? rows : [];
}

export async function createPartyHallQuote(payload) {
    const res = await axiosRestaurantInstance.post(
        RESTAURANT_API_PATHS.partyHallQuotes,
        payload,
        { headers: authHeaders() }
    );
    return {
        partyHallQuoteId: res.data?.partyHallQuoteId,
        quote: normalizePartyHallQuote(res.data?.quote ?? res.data),
        message: res.data?.message,
    };
}

export async function updatePartyHallQuote(quoteId, payload) {
    const res = await axiosRestaurantInstance.put(
        RESTAURANT_API_PATHS.partyHallQuoteById(quoteId),
        payload,
        { headers: authHeaders() }
    );
    return normalizePartyHallQuote(res.data?.quote ?? res.data);
}

export function normalizePartyHallImage(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const id = pickValue(raw, 'imageId', 'Image_Id');
    if (id == null) return null;
    return {
        imageId: Number(id),
        partyHallId: pickValue(raw, 'partyHallId', 'Party_Hall_Id'),
        imageUrl: pickValue(raw, 'imageUrl', 'Image_Url') ?? '',
        imagePath: pickValue(raw, 'imagePath', 'Image_Path') ?? '',
        imageName: pickValue(raw, 'imageName', 'Image_Name') ?? '',
        uploadedById: pickValue(raw, 'uploadedById', 'Uploaded_By_Id'),
        uploadedDate: pickValue(raw, 'uploadedDate', 'Uploaded_Date') ?? '',
        updatedById: pickValue(raw, 'updatedById', 'Updated_By_Id'),
        updatedDate: pickValue(raw, 'updatedDate', 'Updated_Date') ?? '',
    };
}

export async function fetchPartyHallImages(partyHallId) {
    if (!partyHallId) return [];
    const res = await axiosRestaurantInstance.get(
        RESTAURANT_API_PATHS.partyHallImagesByHallId(partyHallId),
        { headers: authHeaders() }
    );
    const rows = res.data?.images ?? [];
    return Array.isArray(rows) ? rows.map(normalizePartyHallImage).filter(Boolean) : [];
}

export async function uploadPartyHallImage({ partyHallId, image, uploadedById }) {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('partyHallId', String(partyHallId));
    formData.append('uploadedById', String(uploadedById ?? ''));
    const res = await axiosRestaurantInstance.post(
        RESTAURANT_API_PATHS.partyHallImageUpload,
        formData,
        {
            headers: {
                ...authHeaders(),
                'Content-Type': 'multipart/form-data',
            },
        }
    );
    return res.data;
}

export async function deletePartyHallImage(imageId) {
    const res = await axiosRestaurantInstance.delete(
        RESTAURANT_API_PATHS.partyHallImageById(imageId),
        { headers: authHeaders() }
    );
    return res.data;
}
